import * as angular from "angular";
import { LimitDataProvider } from "../../../data-provider/limit.dp";
import { UserDataProvider } from "../../../data-provider/user.dp";
import { DialogUtils } from "../../../helper/dialog.helper";
import { Limit } from "../../../models/limit.model";

let InviteEmails = {
    selector: "vcInviteEmails",
    templateUrl:
        "/angular/components/invite/sections/invite-emails.component.html",
    bindings: {
        emails: "=",
        user: "=",
        finishCallback: "&",
        editing: "<?",
    },
    controller: class InviteEmailsController {
        // * bindings for typescript compiler *
        public emails: any;
        public user: any;
        public finishCallback: Function;
        public editing?: boolean;
        // ************************************

        private readonly $scope: any;
        private readonly $filter: any;
        private readonly $timeout: any;
        private readonly $mdConstant: any;
        private dialog: DialogUtils;

        public separatorKeys: string[];
        public invalidEmails: string[] = [];
        public error = "";
        public errorMessage = "";
        public usersCount: object;
        private limitDataProvider: LimitDataProvider;
        public limits: Limit;

        constructor(
            $scope: any,
            $filter: any,
            $timeout: any,
            $mdConstant: any,
            DialogUtils: DialogUtils,
            UserDataProvider: UserDataProvider,
            LimitDataProvider: LimitDataProvider
        ) {
            this.$scope = $scope;
            this.$filter = $filter;
            this.$timeout = $timeout;
            this.$mdConstant = $mdConstant;
            this.dialog = DialogUtils;

            this.separatorKeys = [
                this.$mdConstant.KEY_CODE.COMMA,
                this.$mdConstant.KEY_CODE.SPACE,
                this.$mdConstant.KEY_CODE.ENTER,
                this.$mdConstant.KEY_CODE.SEMICOLON,
            ];

            UserDataProvider.getUsersCount().then(
                (count: object) => (this.usersCount = count)
            );

            this.limitDataProvider = LimitDataProvider;
            this.limitDataProvider
                .get()
                .then((limits: Limit) => (this.limits = limits));
        }

        confirm($event): void {
            if (
                this.limitDataProvider.licensesUnavailable(
                    this.user.license_type,
                    this.usersCount,
                    this.emails.length
                )
            ) {
                let limitCount =
                    this.user.license_type == "super"
                        ? this.limits.superusers +
                          (this.limits.extrasuperusers ?? 0)
                        : this.limits.users + (this.limits.extrausers ?? 0);
                this.dialog.show({
                    component: "vc-pushed-over-limit",
                    targetEvent: $event,
                    scope: this.$scope,
                    locals: {
                        limit: limitCount,
                        licenseType: this.user.license_type,
                    },
                    size: "med",
                });
            } else {
                this.finishCallback({ stage: "contact" });
            }
        }

        // The current, not-yet-added text in the input.
        public draft: string = "";

        // Typing clears any lingering error so the red outline goes away as the
        // address is being fixed.
        onDraftChange(): void {
            if (this.error) {
                this.error = "";
            }
        }

        inputKeydown(event: any): void {
            const K = this.$mdConstant.KEY_CODE;
            if (event.keyCode === K.ENTER) {
                // Enter is the only key that complains about an invalid address.
                event.preventDefault();
                this.commitDraft(true);
            } else if (
                event.keyCode === K.COMMA ||
                event.keyCode === K.SEMICOLON ||
                event.keyCode === K.SPACE
            ) {
                // Separators: never type the character, and only add when the
                // address is valid — an incomplete one is left untouched (no
                // blanking, no error) so the user can keep typing.
                event.preventDefault();
                this.commitDraft(false);
            }
        }

        // Adds the current draft as a recipient. showError=true surfaces the
        // "invalid" message (Enter); otherwise an invalid draft is left in
        // place silently for the user to finish.
        commitDraft(showError: boolean): void {
            const value = (this.draft || "").trim();
            if (value === "") {
                return;
            }
            if (this.$filter("isValidEmail")(value)) {
                if (this.emails.includes(value)) {
                    this.draft = "";
                    this.error = "";
                    return;
                }
                if (this.emails.length >= 1000) {
                    this.error =
                        "You have reached the max limit of 1,000 email addresses.";
                    return;
                }
                this.emails.push(value);
                this.draft = "";
                this.error = "";
            } else if (showError) {
                this.error = "Invalid email address";
            }
        }

        // Remove/clear mutate the same array md-chips is bound to, so its
        // (hidden) chips stay in sync with the recipients panel.
        removeEmail(index: number): void {
            this.emails.splice(index, 1);
            this.error = "";
        }

        clearAll(): void {
            this.emails.splice(0, this.emails.length);
            this.error = "";
        }

        // Inline edit of an already-added recipient.
        public editingIndex: number = -1;
        public editValue: string = "";

        startEdit(index: number): void {
            this.editingIndex = index;
            this.editValue = this.emails[index];
            this.error = "";
        }

        editKeydown(event: any, index: number): void {
            if (event.keyCode === this.$mdConstant.KEY_CODE.ENTER) {
                event.preventDefault();
                this.commitEdit(index);
            } else if (event.keyCode === this.$mdConstant.KEY_CODE.ESCAPE) {
                this.cancelEdit();
            }
        }

        commitEdit(index: number): void {
            // Guard against the blur that fires when Enter/Escape already
            // ended the edit.
            if (this.editingIndex !== index) {
                return;
            }
            const value = (this.editValue || "").trim();
            if (value === "") {
                // Cleared → drop the recipient.
                this.emails.splice(index, 1);
            } else if (this.$filter("isValidEmail")(value)) {
                this.emails[index] = value;
            } else {
                // Invalid → keep the original address and flag it.
                this.invalidEmailError();
            }
            this.editingIndex = -1;
            this.editValue = "";
        }

        cancelEdit(): void {
            this.editingIndex = -1;
            this.editValue = "";
            this.error = "";
        }

        invalidEmailError(): void {
            this.error = "Invalid email address";
            this.$timeout(() => {
                this.error = "";
            }, 3000); //wait 3 Seconds and hide
        }

        paste(event: any): void {
            event.preventDefault();
            this.invalidEmails = [];
            let emailList = event.clipboardData.getData("Text").split(/;|,|\n/);
            angular.forEach(emailList, (value: string) => {
                let email = value.trim();
                if (email && this.$filter("isValidEmail")(email)) {
                    if (
                        this.emails.length < 1000 &&
                        !this.emails.includes(value.trim())
                    ) {
                        this.emails.push(email); //Push if valid
                    }
                    if (this.emails.length >= 1000) {
                        this.error =
                            "You have reached the max limit of 1,000 email addresses.";
                    }
                } else if (
                    !this.$filter("isValidEmail")(email) &&
                    email.trim() != ""
                ) {
                    this.invalidEmails.push(email);
                }
            });
            if (this.invalidEmails.length > 0) {
                this.error =
                    "Some of your emails were invalid: " +
                    this.invalidEmails.join(", ");
            }
        }
    },
};

angular.module("invite").component(InviteEmails.selector, InviteEmails);
